import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Zap, Play, CheckCircle2, XCircle, Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Checklist = () => {
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [checks, setChecks] = useState(null);
  const [running, setRunning] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  const run = async () => {
    setRunning(true);
    setChecks(null);
    try {
      const res = await axios.get(`${API}/admin/preflight`, { params: { key } });
      const results = [...res.data.checks];

      const video = await fetch("/demo.webm", { method: "HEAD" }).catch(() => null);
      results.push({ name: "Session preview video", ok: !!video && video.ok, detail: video && video.ok ? "Loads and plays" : "Failed to load" });

      const og = await fetch("/og-image.png", { method: "HEAD" }).catch(() => null);
      results.push({ name: "Share preview card", ok: !!og && og.ok, detail: og && og.ok ? "Image served" : "Missing" });

      setChecks(results);
    } catch (e) {
      if (e.response?.status === 401) setUnauthorized(true);
    } finally {
      setRunning(false);
    }
  };

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-obsidian grid place-items-center px-6">
        <div data-testid="checklist-unauthorized" className="text-center">
          <ShieldAlert className="h-10 w-10 text-volt mx-auto" />
          <h1 className="font-display font-black uppercase text-3xl mt-6">Unauthorized</h1>
        </div>
      </div>
    );
  }

  const passed = checks ? checks.filter((c) => c.ok).length : 0;

  return (
    <div data-testid="checklist-page" className="min-h-screen bg-obsidian relative">
      <div className="absolute inset-0 grid-lines" />
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-8 w-8 bg-volt text-white">
            <Zap className="h-4 w-4" strokeWidth={3} />
          </span>
          <span className="font-display font-black text-2xl uppercase">Fit<span className="text-volt">/</span>50 <span className="text-neutral-500 text-lg">Pre-Flight</span></span>
        </div>
        <p className="mt-3 text-sm text-neutral-500">One click before you open the doors. Payments, email, counters, video — all checked live.</p>

        <button
          data-testid="run-preflight-button"
          onClick={run}
          disabled={running}
          className="mt-8 inline-flex items-center gap-3 bg-volt text-white font-display font-bold uppercase tracking-wide px-8 py-4 hover:bg-volt-hover hover:shadow-[0_0_35px_rgba(255,107,0,0.5)] disabled:opacity-60 transition-[background-color,box-shadow] duration-200"
        >
          {running ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5 fill-current" />}
          {running ? "Running Checks..." : "Run Pre-Flight"}
        </button>

        {checks && (
          <div className="mt-8">
            <p data-testid="preflight-summary" className={`font-mono text-xs tracking-[0.25em] uppercase ${passed === checks.length ? "text-volt" : "text-destructive"}`}>
              {passed} / {checks.length} checks passed
            </p>
            <div className="mt-4 space-y-3">
              {checks.map((c, i) => (
                <div
                  key={c.name}
                  data-testid={`preflight-check-${i}`}
                  className={`flex items-center justify-between gap-4 border p-5 ${c.ok ? "border-white/10 bg-carbon" : "border-destructive/60 bg-destructive/5"}`}
                >
                  <div>
                    <p className="font-display font-bold uppercase tracking-wide">{c.name}</p>
                    <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 mt-1">{c.detail}</p>
                  </div>
                  {c.ok ? <CheckCircle2 className="h-6 w-6 text-volt shrink-0" /> : <XCircle className="h-6 w-6 text-destructive shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to={`/admin?key=${key}`} className="mt-8 inline-block font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors">
          ← Back to sales dashboard
        </Link>
      </div>
    </div>
  );
};
